import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, MapPin, Percent } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import useRegionStore from '@/stores/regionStore'
import { configService } from '@/services/configService'

interface RegionType {
  id: string
  code: string
  name: string
  description?: string
  enabled: boolean
}

interface Country {
  id: string
  code: string
  name: string
  native_name?: string
  enabled: boolean
}

const RegionForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const {
    currentRegion,
    loading,
    error,
    fetchRegionById,
    createRegion,
    updateRegion,
    clearError,
    clearCurrentRegion
  } = useRegionStore()

  const [formData, setFormData] = useState({
    country_id: '',
    code: '',
    name: '',
    type_id: '',
    tax_rate: 0,
    enabled: true
  })

  const [regionTypes, setRegionTypes] = useState<RegionType[]>([])
  const [countries, setCountries] = useState<Country[]>([])
  const [loadingTypes, setLoadingTypes] = useState(false)
  const [loadingCountries, setLoadingCountries] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Charger les types de région
  useEffect(() => {
    const loadRegionTypes = async () => {
      try {
        setLoadingTypes(true)
        // Note: Nous devons ajouter un endpoint pour récupérer les types de région
        // Pour l'instant, nous allons utiliser une liste statique
        const response = await fetch('/api/config/region-types')
        if (response.ok) {
          const data = await response.json()
          setRegionTypes(data.data || [])
        } else {
          // Fallback à une liste statique si l'API n'est pas disponible
          setRegionTypes([
            { id: 'state', code: 'state', name: 'État', enabled: true },
            { id: 'province', code: 'province', name: 'Province', enabled: true },
            { id: 'region', code: 'region', name: 'Région', enabled: true },
            { id: 'territory', code: 'territory', name: 'Territoire', enabled: true }
          ])
        }
      } catch (error) {
        console.error('Error loading region types:', error)
        // Fallback à une liste statique
        setRegionTypes([
          { id: 'state', code: 'state', name: 'État', enabled: true },
          { id: 'province', code: 'province', name: 'Province', enabled: true },
          { id: 'region', code: 'region', name: 'Région', enabled: true },
          { id: 'territory', code: 'territory', name: 'Territoire', enabled: true }
        ])
      } finally {
        setLoadingTypes(false)
      }
    }

    loadRegionTypes()
  }, [])

  // Charger les pays
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true)
        const countriesData = await configService.countries.getEnabled()
        setCountries(countriesData)
      } catch (error) {
        console.error('Error loading countries:', error)
        setCountries([])
      } finally {
        setLoadingCountries(false)
      }
    }

    loadCountries()
  }, [])

  useEffect(() => {
    if (isEditMode && id) {
      fetchRegionById(id)
    }

    return () => {
      clearCurrentRegion()
    }
  }, [id, isEditMode])

  useEffect(() => {
    if (isEditMode && currentRegion) {
      setFormData({
        country_id: currentRegion.country_id,
        code: currentRegion.code,
        name: currentRegion.name,
        type_id: currentRegion.type_id,
        tax_rate: currentRegion.tax_rate,
        enabled: currentRegion.enabled
      })
    }
  }, [currentRegion, isEditMode])

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!formData.country_id.trim()) {
      errors.country_id = 'L\'identifiant du pays est requis'
    }

    if (!formData.code.trim()) {
      errors.code = 'Le code est requis'
    }

    if (!formData.name.trim()) {
      errors.name = 'Le nom est requis'
    }

    if (formData.tax_rate < 0 || formData.tax_rate > 100) {
      errors.tax_rate = 'Le taux de taxe doit être entre 0 et 100'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      if (isEditMode && id) {
        await updateRegion(id, formData)
        alert('Région mise à jour avec succès')
      } else {
        await createRegion(formData)
        alert('Région créée avec succès')
      }
      navigate('/regions')
    } catch (error: any) {
      console.error('Error saving region:', error)
      // L'erreur est déjà gérée par le store
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Obtenir le nom du type de région
  const getTypeName = () => {
    if (!formData.type_id) return 'Non spécifié'
    const type = regionTypes.find(t => t.id === formData.type_id)
    return type ? type.name : 'Type inconnu'
  }

  if (loading && isEditMode) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-gray-200 animate-pulse rounded" />
        <div className="h-96 w-full bg-gray-200 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditMode ? 'Modifier la région' : 'Nouvelle région'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode 
              ? 'Modifiez les informations de la région' 
              : 'Créez une nouvelle région pour le système'}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/regions')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Informations de la région</CardTitle>
          <CardDescription>
            Remplissez les informations de la région. Les champs marqués d'un * sont obligatoires.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Country */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Pays *
                  <span className="text-xs text-muted-foreground ml-2">(Sélectionnez un pays)</span>
                </label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.country_id}
                  onChange={(e) => handleChange('country_id', e.target.value)}
                  disabled={loadingCountries}
                >
                  <option value="">Sélectionnez un pays</option>
                  {countries
                    .filter(country => country.enabled)
                    .map(country => (
                      <option key={country.id} value={country.id}>
                        {country.name} ({country.code}){country.native_name ? ` - ${country.native_name}` : ''}
                      </option>
                    ))}
                </select>
                {loadingCountries && (
                  <p className="text-sm text-muted-foreground">Chargement des pays...</p>
                )}
                {formErrors.country_id && (
                  <p className="text-sm text-red-500">{formErrors.country_id}</p>
                )}
              </div>

              {/* Code */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Code *
                  <span className="text-xs text-muted-foreground ml-2">(ex: CA-ON pour Ontario, Canada)</span>
                </label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                  placeholder="CA-ON"
                  className={formErrors.code ? 'border-red-500' : ''}
                />
                {formErrors.code && (
                  <p className="text-sm text-red-500">{formErrors.code}</p>
                )}
              </div>

              {/* Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Nom complet *
                  <span className="text-xs text-muted-foreground ml-2">(ex: Ontario, Californie, Île-de-France)</span>
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Ontario"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>

              {/* Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Type *</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.type_id}
                  onChange={(e) => handleChange('type_id', e.target.value)}
                  disabled={loadingTypes}
                >
                  <option value="">Sélectionnez un type</option>
                  {regionTypes
                    .filter(type => type.enabled)
                    .map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({type.code})
                      </option>
                    ))}
                </select>
                {loadingTypes && (
                  <p className="text-sm text-muted-foreground">Chargement des types...</p>
                )}
              </div>

              {/* Tax Rate */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Taux de taxe (%)
                  <span className="text-xs text-muted-foreground ml-2">(0-100)</span>
                </label>
                <div className="relative">
                  <Percent className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="tax_rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => handleChange('tax_rate', parseFloat(e.target.value) || 0)}
                    className={`pl-9 ${formErrors.tax_rate ? 'border-red-500' : ''}`}
                  />
                </div>
                {formErrors.tax_rate && (
                  <p className="text-sm text-red-500">{formErrors.tax_rate}</p>
                )}
              </div>
            </div>

            {/* Enabled Switch */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="enabled" className="text-sm font-medium cursor-pointer">
                Région active
              </label>
            </div>

            {/* Preview */}
            <div className="mt-8 p-4 border rounded-lg bg-muted/50">
              <h3 className="text-lg font-semibold mb-2">Aperçu de la région</h3>
              <div className="flex items-center space-x-4">
                <MapPin className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Région créée:</p>
                  <p className="text-2xl font-bold">{formData.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Code: {formData.code} • Type: {getTypeName()} • Taxe: {formData.tax_rate}%
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/regions')}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                <Save className="mr-2 h-4 w-4" />
                {loading ? 'Enregistrement...' : isEditMode ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default RegionForm
